import { useMemo } from 'react';

import { useDatasetRunsAndExamples } from '@/Pages/Settings/utils/useRecentRuns';
import { SearchModel } from '@/components/RunsTable/types';
import { useRunsQuery } from '@/hooks/useSwr';

export const useDatasetInputOutputData = (datasetId?: string) => {
  const { recentRuns, runIdsToExample, isLoading, examples } =
    useDatasetRunsAndExamples(datasetId);

  const result = useMemo(() => {
    const runWithInputOutput =
      recentRuns.data?.runs.find((run) => run.inputs && run.outputs) ??
      recentRuns.data?.runs[0];

    const runExample = runWithInputOutput
      ? runIdsToExample[runWithInputOutput.id]
      : examples.data?.[0];

    return {
      input: runExample?.inputs,
      output: runWithInputOutput?.outputs,
      referenceOutput: runExample?.outputs,
      isLoading,
    };
  }, [recentRuns, runIdsToExample, examples, isLoading]);

  return result;
};

export const useSessionInputOutputData = ({
  sessionId,
  filters,
}: {
  sessionId?: string;
  filters: SearchModel;
}) => {
  const recentRuns = useRunsQuery(
    sessionId
      ? {
          session: [sessionId],
          limit: 10,
          filter: filters?.filter,
          trace_filter: filters?.traceFilter,
          tree_filter: filters?.treeFilter,
          skip_prev_cursor: true,
        }
      : null,
    undefined,
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  return useMemo(() => {
    const runWithInputOutput =
      recentRuns.data?.runs.find((run) => run.inputs && run.outputs) ??
      recentRuns.data?.runs[0];
    return {
      input: runWithInputOutput?.inputs,
      output: runWithInputOutput?.outputs,
      isLoading: recentRuns.isLoading,
    };
  }, [recentRuns]);
};
